import * as THREE from 'three';
import type { Point, UnitState } from '../../../shared/game/world-types.js';
import { HERO_ACTION_SECONDS } from '../../../shared/game/combat-types.js';
import { deathRagdoll as tuning } from '../../../content/characters/rigs/death-ragdoll.js';
import type { HitImpact } from './character-hit.js';
import type { GameplayUnitAnimator, UnitAnimator } from './unit-animator.js';
import type { DeathRig } from './death-rig.js';
import { DeathRagdoll } from './death-ragdoll.js';
import { CharacterAnimator } from './character-animator.js';
import type { CharacterRig } from './character-rig.js';
import { getUpTurn, type GetUpFace, type GetUpFrom } from './get-up-pose.js';

/** A blow this recent (seconds) is the one that knocked the unit down: it pushes the fall. */
const KNOCKDOWN_CUE_SECONDS = 1.5;
/** A shove pushes like a blow of this much damage (piercing: straight away from whoever shoved). */
const SHOVE_DAMAGE = 10;
/** Share of the get-up over which the arms and legs of a body with its own get-up hand over from the ragdoll. */
const LIMB_HANDOVER = 0;

/** The pose a knocked-down body lay in when it began to get up, held so the get-up can blend out of it. */
interface Lying {
  bones: {
    bone: THREE.Object3D; position: THREE.Vector3; rotation: THREE.Quaternion; /** Set for a bone whose parent the ragdoll did not move: its pose relative to the unit. */ anchored?: THREE.Matrix4;
    /** The lying and the animator's rotations last frame, sign-matched, so the blend keeps one way round as they move (see `turnToward`). */ was?: THREE.Quaternion; toward?: THREE.Quaternion;
    /** An arm or leg bone of a body that solves its limbs to where the hands and feet lay: it hands over almost at once. */ limb?: boolean;
    /** The bone's own transform before the fall: laid back each frame before the animator poses, since it does not write every bone. */ rest?: { position: THREE.Vector3; rotation: THREE.Quaternion };
  }[];
  /** Root yaw (in the unit's frame) at which the authored lying pose matches the ragdoll's, for bodies that author their own get-up. */
  yaw?: number; face: GetUpFace;
  /** How the body lay, in the root frame turned to `yaw`, for a body with its own get-up. */
  from?: GetUpFrom;
}

/**
 * Blends from the lying rotation toward the animator's, `weight` of the way. A plain slerp takes the shorter way round, so a limb whose lying and
 * animated rotations are about half a turn apart would flip from one way to the other mid-blend; this picks the way at the start and keeps it.
 */
function turnToward(lying: THREE.Quaternion, held: Lying['bones'][number], weight: number) {
  // A rotation read back from a matrix (a bone held relative to the unit) can come back as either sign: keep both ends on their own track.
  const from = lying.clone(), to = held.bone.quaternion.clone();
  if (held.was && from.dot(held.was) < 0) from.set(-from.x, -from.y, -from.z, -from.w);
  if (to.dot(held.toward ?? from) < 0) to.set(-to.x, -to.y, -to.z, -to.w);
  held.was = from.clone(); held.toward = to.clone();
  const cos = THREE.MathUtils.clamp(from.dot(to), -1, 1), angle = Math.acos(cos), sin = Math.sin(angle);
  if (sin < 1e-5) return to.slerp(from, 1 - weight);
  const a = Math.sin((1 - weight) * angle) / sin, b = Math.sin(weight * angle) / sin;
  return new THREE.Quaternion(from.x * a + to.x * b, from.y * a + to.y * b, from.z * a + to.z * b, from.w * a + to.w * b).normalize();
}

/** Mandatory gameplay death ownership, installed once by the unit factory for every family. It also owns the Prone fall, the lying body and the get-up. */
export function useUnitDeathPhysics(animator: UnitAnimator, rig: DeathRig): GameplayUnitAnimator {
  let doll: DeathRagdoll | undefined, time: number | undefined, anchor: Point | undefined;
  let cue: { at: number; from: Point; impact: HitImpact } | undefined;
  /** The last blow, which may have knocked the unit down. */
  let blow: { at: number; from: Point; impact: HitImpact } | undefined;
  /** A Prone body's ragdoll: falling, then holding the pose it settled in. */
  let down: DeathRagdoll | undefined;
  /** Getting up; `cleared` once the unit is no longer Prone, after which Prone again is a new knockdown. */
  let rising: { at: number; seconds: number; lying: Lying; cleared: boolean } | undefined;
  let disposed = false;
  const authored = animator instanceof CharacterAnimator;
  const update = animator.update.bind(animator), reset = animator.reset.bind(animator), dispose = animator.dispose.bind(animator), play = animator.play.bind(animator);
  const channelOrb = animator.channelOrb?.bind(animator), spellOrigin = animator.spellOrigin?.bind(animator), cantripCast = animator.cantripCast?.bind(animator), sever = animator.sever?.bind(animator);
  const stand = () => { down?.dispose(); down = undefined; rising = undefined; animator.getUp = undefined; };
  const clear = () => { doll?.dispose(); doll = undefined; anchor = undefined; cue = undefined; stand(); };
  Object.defineProperties(animator, {
    deathState: { get: () => doll ? doll.settled ? 'settled' : 'falling' : 'alive' },
    settledCorpse: { get: () => !!doll?.settled, configurable: true },
    proneState: { get: () => down ? down.settled ? 'lying' : 'falling' : rising ? 'rising' : 'standing' },
  });
  animator.deathImpact = (at, from, impact) => { cue = { at, from: { ...from }, impact }; };
  // A shove without damage (the scenario viewer's Prone preview) pushes the fall away from whoever gave it, unless a blow already did.
  animator.shove = (at, from) => { if (!blow || Math.abs(at - blow.at) >= KNOCKDOWN_CUE_SECONDS) blow = { at, from: { ...from }, impact: { damage: SHOVE_DAMAGE, type: 'piercing', critical: false } }; };
  animator.play = (...args) => {
    const [kind, at, from, , impact] = args;
    if (kind === 'hit' && from && impact) blow = { at, from: { ...from }, impact };
    play(...args);
  };
  const ragdoll = (position: Point, impact: { from: Point; impact: HitImpact } | undefined, options?: Parameters<typeof DeathRagdoll.createRig>[6]) => {
    // Without a blow the body topples backward.
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(rig.root.getWorldQuaternion(new THREE.Quaternion()));
    const direction = impact ? { x: position.x - impact.from.x, z: position.z - impact.from.z } : { x: forward.x, z: forward.z };
    return DeathRagdoll.createRig(rig, animator.groundHeight, animator.ragdollMap, direction, impact?.impact,
      !!rig.root.getObjectByName('neck-stump'), options);
  };
  /** Knocked Prone: the body falls through the same physics as a death, pushed by the blow that did it, at a share of a killing blow's force. */
  const knockDown = (state: UnitState, position: Point, gameTime: number) => {
    if (time === undefined && state.heading !== undefined) rig.root.rotation.y = state.heading;
    const recent = blow && Math.abs(gameTime - blow.at) < KNOCKDOWN_CUE_SECONDS ? { from: blow.from, impact: { ...blow.impact, damage: blow.impact.damage * tuning.proneImpulse } } : undefined;
    rising = undefined; animator.getUp = undefined;
    down = ragdoll(position, recent); blow = undefined; animator.stopActions();
    // A unit seen lying when it joins (or after a rewind) is already down.
    if (time === undefined) down.settle();
  };
  /** Getting up: hold the lying pose, give the bones back to the animator and let its get-up (or, for a family without one, a blend) take over. */
  const getUp = (state: UnitState, gameTime: number) => {
    const windup = state.combat.windup?.kind === 'stand' ? state.combat.windup : undefined;
    const lying = capture();
    down?.dispose(); down = undefined; reset();
    for (const held of lying.bones) held.rest = { position: held.bone.position.clone(), rotation: held.bone.quaternion.clone() };
    rising = { at: gameTime - (windup ? windup.duration - windup.remaining : 0), seconds: windup?.duration ?? HERO_ACTION_SECONDS, lying, cleared: !state.prone };
  };
  const capture = (): Lying => {
    const unit = rig.root.parent;
    rig.root.updateWorldMatrix(true, true);
    const toUnit = unit ? unit.matrixWorld.clone().invert() : new THREE.Matrix4();
    const moved = new Set(rig.segments.map(segment => segment.bone));
    const limbs = new Set(animator instanceof CharacterAnimator ? [animator.rig.leftArm, animator.rig.rightArm, animator.rig.leftLeg, animator.rig.rightLeg].flatMap(limb => [limb.upper, limb.lower, limb.hand]) : []);
    const bones = rig.segments.map(({ bone }) => ({ bone, position: bone.position.clone(), rotation: bone.quaternion.clone(),
      ...!moved.has(bone.parent!) ? { anchored: toUnit.clone().multiply(bone.matrixWorld) } : {}, ...limbs.has(bone) ? { limb: true } : {} }));
    // Which way the body lies: the trunk's line from the hips to the head, and whether the chest faces the sky or the ground.
    const trunk = rig.segments[0].bone, head = rig.head ?? rig.segments[1]?.bone ?? trunk, chest = rig.segments[1]?.bone ?? trunk;
    const at = (bone: THREE.Object3D) => bone.getWorldPosition(new THREE.Vector3()).applyMatrix4(toUnit);
    const line = at(head).sub(at(trunk));
    const facing = new THREE.Vector3(0, 0, 1).transformDirection(chest.matrixWorld).transformDirection(toUnit);
    const face: GetUpFace = facing.y < 0 ? 'down' : 'up';
    // The authored lying pose has the head ahead of the hips on the front, behind them on the back.
    const yaw = face === 'down' ? Math.atan2(line.x, line.z) : Math.atan2(-line.x, -line.z);
    if (!(animator instanceof CharacterAnimator)) return { bones, face };
    const body = animator.rig, toRoot = new THREE.Matrix4().compose(rig.root.position, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw), rig.root.scale).invert();
    const lay = (bone: THREE.Object3D) => at(bone).applyMatrix4(toRoot);
    // Which way the middle joint points off the line from the shoulder or hip to the wrist or ankle.
    const bend = (limb: CharacterRig['leftArm']) => {
      const start = lay(limb.upper), middle = lay(limb.lower), end = lay(limb.hand), line = end.clone().sub(start).normalize();
      const off = middle.sub(start); off.sub(line.multiplyScalar(off.dot(line)));
      return off.length() > .02 ? off.normalize() : undefined;
    };
    const hips = new THREE.Vector3(), pelvis = new THREE.Quaternion();
    toRoot.clone().multiply(toUnit).multiply(body.hips.matrixWorld).decompose(hips, pelvis, new THREE.Vector3());
    const spine = body.joints.get('spine'), neck = body.joints.get('neck');
    return { bones, face, yaw, from: { hips, pelvis, spine: spine?.quaternion.clone(), chest: body.torso.quaternion.clone(), neck: neck?.quaternion.clone(), head: body.head.quaternion.clone(),
      hands: [lay(body.leftArm.hand), lay(body.rightArm.hand)], feet: [lay(body.leftLeg.hand), lay(body.rightLeg.hand)],
      elbows: [bend(body.leftArm), bend(body.rightArm)], knees: [bend(body.leftLeg), bend(body.rightLeg)] } };
  };
  /** Lays the lying pose under the animator's: `weight` 0 is the body as it lay, 1 the animator's pose. */
  const blendFrom = (lying: Lying, weight: number, limbWeight = weight) => {
    const unit = rig.root.parent;
    rig.root.updateWorldMatrix(true, true);
    const local = new THREE.Matrix4(), position = new THREE.Vector3(), rotation = new THREE.Quaternion(), scale = new THREE.Vector3();
    for (const held of lying.bones) {
      if (held.anchored) {
        // Its parent belongs to the animator (the root turns as the figure gets up): keep where the bone lay relative to the unit.
        local.copy(held.bone.parent!.matrixWorld).invert();
        if (unit) local.multiply(unit.matrixWorld);
        local.multiply(held.anchored).decompose(position, rotation, scale);
      } else { position.copy(held.position); rotation.copy(held.rotation); }
      const share = held.limb ? limbWeight : weight;
      held.bone.position.lerpVectors(position, held.bone.position, share);
      held.bone.quaternion.copy(turnToward(rotation, held, share));
      // Bones further down the list hang from this one (through bones the ragdoll does not move, such as the spine).
      held.bone.updateWorldMatrix(false, true);
    }
  };
  animator.update = (...args) => {
    if (disposed) return;
    const [state, position, gameTime] = args;
    if (time !== undefined && gameTime < time) { clear(); reset(); time = undefined; }
    if (!state.health.dead) {
        if (doll) { clear(); reset(); }
      const standing = !state.prone || state.combat.windup?.kind === 'stand';
      if (rising && !state.prone) rising.cleared = true;
      // Knocked down again once up (even while the get-up is still playing): fall from wherever the body is.
      if (state.prone && !down && (!rising || rising.cleared)) knockDown(state, position, gameTime);
      if (down) {
        if (!standing) { if (time !== undefined) down.update(gameTime - time); time = gameTime; return; }
        // The stand-up begins (or Prone ends without one): an unfinished fall goes straight into the get-up.
        getUp(state, gameTime);
      }
      if (rising) {
        const progress = THREE.MathUtils.clamp((gameTime - rising.at) / rising.seconds, 0, 1);
        animator.getUp = authored ? { progress, face: rising.lying.face, from: rising.lying.from } : undefined;
        for (const { bone, rest } of rising.lying.bones) if (rest) { bone.position.copy(rest.position); bone.quaternion.copy(rest.rotation); }
        update(...args);
        const lying = rising.lying;
        if (lying.yaw !== undefined) {
          // The figure gets up the way it lay and turns to the way the unit faces as it rises.
          const facing = rig.root.rotation.y, turn = Math.atan2(Math.sin(lying.yaw - facing), Math.cos(lying.yaw - facing));
          rig.root.rotation.y = facing + turn * (1 - getUpTurn(progress));
        }
        // A body with its own get-up starts it from the way it lay (trunk, wrists and ankles), so only the swivel of an elbow or knee and the turn
        // of a hand or foot are left to blend; a family without one blends from the lying pose to standing over the whole stand-up.
        if (authored) blendFrom(lying, 1, (LIMB_HANDOVER ? THREE.MathUtils.smoothstep(progress, 0, LIMB_HANDOVER) : 1));
        else blendFrom(lying, THREE.MathUtils.smoothstep(progress, 0, .85));
        if (progress >= 1) { rising = undefined; animator.getUp = undefined; }
        time = gameTime; return;
      }
      update(...args); time = gameTime; return;
    }
    const relocated = anchor && (anchor.x !== state.x || anchor.z !== state.z);
    if (relocated) { clear(); reset(); }
    if (!doll) {
      if (time === undefined && state.heading !== undefined) rig.root.rotation.y = state.heading;
      const impact = cue && Math.abs(gameTime - cue.at) < 2 ? cue : undefined;
      // A body killed on the ground keeps the pose it lay in (and the standing pose it fell from, for a revival) without a fresh topple.
      const lying = down?.handOver();
      down = undefined; rising = undefined; animator.getUp = undefined;
      const motion = !lying && time !== undefined ? animator.hitMotion?.(state, position, gameTime) : undefined;
      doll = ragdoll(position, impact, { ...(lying ? { rest: lying, topple: false } : {}), motion });
      anchor = { x: state.x, z: state.z }; cue = undefined; animator.stopActions();
      rig.root.traverse(object => { if (object.name === 'arrow') object.visible = false; });
      // Joined, rewound and relocated corpses use a settled physical pose without replaying an old kill.
      if (time === undefined || relocated) doll.settle();
    } else if (time !== undefined) doll.update(gameTime - time);
    time = gameTime;
  };
  if (channelOrb) animator.channelOrb = () => doll || down ? undefined : channelOrb();
  if (spellOrigin) animator.spellOrigin = () => doll || down ? undefined : spellOrigin();
  if (cantripCast) animator.cantripCast = () => doll || down ? undefined : cantripCast();
  if (sever) animator.sever = () => { doll?.sever(); return sever(); };
  animator.reset = () => { clear(); time = undefined; blow = undefined; reset(); };
  animator.dispose = () => { if (disposed) return; clear(); disposed = true; dispose(); };
  return animator as GameplayUnitAnimator;
}
